import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCurrency } from '../../../../utils/formatters';
import type { Info360Documento, Info360Indicadores } from '../types';
import Info360DocumentoList from './Info360DocumentoList';

interface Props {
  data: Info360Indicadores;
  onAbrirDocumento?: (documento: Info360Documento) => void;
}

const Info360TabVentas: React.FC<Props> = ({ data, onAbrirDocumento }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hayVentas = data.ventasMensuales.some((item) => item.importe !== 0);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Evolución de ventas (12 meses)
        </Typography>
        {hayVentas ? (
          <Box sx={{ width: '100%', height: isMobile ? 220 : 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.ventasMensuales} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  width={isMobile ? 48 : 64}
                  tickFormatter={(v) => `${Math.round(Number(v) / 1000)}k`}
                />
                <RechartsTooltip
                  formatter={(value: number) => [formatCurrency(value), 'Ventas']}
                />
                <Bar dataKey="importe" fill="#1976d2" radius={[4, 4, 0, 0]} name="Ventas" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
            No hay ventas registradas en los últimos 12 meses.
          </Typography>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Productos más comprados
            </Typography>
            <Stack spacing={1.25}>
              {data.productosTop.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin productos para mostrar.
                </Typography>
              ) : data.productosTop.map((p) => (
                <Stack key={p.descripcion} direction="row" justifyContent="space-between" spacing={1}>
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {p.descripcion}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.cantidad} u.
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} whiteSpace="nowrap">
                    {formatCurrency(p.importe)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Info360DocumentoList
            titulo="Últimas facturas"
            documentos={data.ultimasFacturas}
            emptyText="Sin facturas recientes."
            onAbrirDocumento={onAbrirDocumento}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

export default Info360TabVentas;
